import { Loader2, RotateCcw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Money } from "@workspace/accounting/components";
import { CheckOperation } from "@workspace/operations/check-operation";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { FormattedDate } from "@workspace/ui/components/formatted-date";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { Separator } from "@workspace/ui/components/separator";

import { eposApi } from "../../api";
import { useKioskContext } from "../../kiosk";
import type { Order } from "../../types";
import { useEposContext } from "../epos-context";
import { taxBreakdown } from "../order-tax";
import { PrintReceiptButton } from "./print-receipt-button";

interface ClosedOrderDetailDialogProps {
	orderId: string | null;
	onClose: () => void;
}

function getStatusBadge(status: Order["status"]) {
	switch (status) {
		case "Completed":
			return (
				<Badge variant="secondary" className="bg-green-100 text-green-800">
					Completed
				</Badge>
			);
		case "Paid":
			return (
				<Badge variant="secondary" className="bg-blue-100 text-blue-800">
					Paid
				</Badge>
			);
		case "Refunded":
			return <Badge variant="destructive">Refunded</Badge>;
		case "PartiallyRefunded":
			return (
				<Badge variant="secondary" className="bg-amber-100 text-amber-800">
					Partially Refunded
				</Badge>
			);
		case "Closed":
			return (
				<Badge variant="secondary" className="bg-gray-100 text-gray-800">
					Closed
				</Badge>
			);
		case "Cancelled":
			return <Badge variant="outline">Cancelled</Badge>;
		default:
			return <Badge variant="outline">{status}</Badge>;
	}
}

export function ClosedOrderDetailDialog({
	orderId,
	onClose,
}: ClosedOrderDetailDialogProps) {
	const {
		tenant,
		timeZone,
		operations,
		setRefundOrder,
		setIsRefundDialogOpen,
	} = useEposContext();
	const { venueId } = useKioskContext();

	const { data: order, isLoading } = useQuery(
		eposApi.getOrderDetailOptions(tenant, venueId, orderId),
	);

	// HA-657: contained VAT is "Includes VAT"; only tax added on top (an order rung before) is "Tax".
	const taxParts = order ? taxBreakdown(order) : { onTop: 0, included: 0 };

	const isRefundable =
		order &&
		["Paid", "Completed", "Closed", "PartiallyRefunded"].includes(
			order.status,
		) &&
		order.refundableBalance.value > 0;

	const handleProcessRefund = () => {
		if (!order) return;

		// Convert OrderDetailResponse to Order shape for the refund flow
		setRefundOrder({
			id: order.id,
			tabId: order.tabId,
			tableId: order.tableId ?? undefined,
			tableName: order.tableName ?? undefined,
			type: order.type,
			status: order.status,
			items: order.items,
			subtotal: order.subtotal,
			tax: order.tax,
			total: order.total,
			totalPaid: order.totalPaid,
			tip: order.tip ?? undefined,
			currency: order.currency,
			createdAt: order.createdAt,
			updatedAt: order.updatedAt ?? order.createdAt,
			completedAt: order.completedAt ?? undefined,
			payments: order.payments,
			refunds: order.refunds,
			refundedTotal: order.refundedTotal,
			refundableBalance: order.refundableBalance,
		} as Order);
		setIsRefundDialogOpen(true);
		onClose();
	};

	return (
		<Dialog
			open={!!orderId}
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col">
				<DialogHeader>
					<DialogTitle>
						Order #{orderId?.slice(-6)}
						{order && (
							<span className="ml-2 inline-flex align-middle">
								{getStatusBadge(order.status)}
							</span>
						)}
					</DialogTitle>
					{order && (
						<DialogDescription>
							<span className="flex items-center gap-2 mt-1">
								<Money {...order.total} />
								{order.tabName && <span>&middot; {order.tabName}</span>}
								{!order.tabName && order.tabNumber && (
									<span>&middot; Tab #{order.tabNumber}</span>
								)}
							</span>
						</DialogDescription>
					)}
				</DialogHeader>

				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				) : order ? (
					<ScrollArea className="flex-1 -mx-6 px-6">
						<div className="space-y-4">
							{/* Items */}
							<div className="rounded-lg border p-3 space-y-2">
								<h4 className="text-sm font-medium">
									Items ({order.items.length})
								</h4>
								<Separator />
								{order.items.map((item) => (
									<div key={item.id} className="flex justify-between text-sm">
										<div>
											<span>
												{item.quantity}x {item.name}
											</span>
											{item.selectedOptions?.map((group) =>
												group.selectedItems.map((opt) => (
													<span
														key={opt.id}
														className="block text-xs text-muted-foreground ml-4"
													>
														+ {opt.name}
														{opt.priceImpact.value > 0 && (
															<>
																{" "}
																(<Money {...opt.priceImpact} />)
															</>
														)}
													</span>
												)),
											)}
										</div>
										<Money {...item.price} />
									</div>
								))}
							</div>

							{/* Payments */}
							{order.payments.length > 0 && (
								<div className="rounded-lg border p-3 space-y-2">
									<h4 className="text-sm font-medium">Payments</h4>
									<Separator />
									{order.payments.map((payment) => (
										<div
											key={payment.id}
											className="flex justify-between text-sm"
										>
											<div>
												<span className="capitalize">{payment.method}</span>
												<span className="text-xs text-muted-foreground ml-2">
													{payment.status}
												</span>
												{/* HA-859: the payment's tip (a Pay Tab tip's share for this order), as the till shows it on an open order. */}
												{payment.tip && payment.tip.value > 0 && (
													<span className="text-xs text-muted-foreground ml-2">
														(tip: <Money {...payment.tip} />)
													</span>
												)}
											</div>
											<div className="text-right">
												<Money {...payment.amount} />
												<div className="text-xs text-muted-foreground">
													<FormattedDate
														date={payment.timestamp}
														timeZone={timeZone}
														options={{
															hour: "2-digit",
															minute: "2-digit",
															hour12: false,
														}}
													/>
												</div>
											</div>
										</div>
									))}
								</div>
							)}

							{/* Totals */}
							<div className="rounded-lg border p-3 space-y-2">
								<div className="flex justify-between text-sm">
									<span className="text-muted-foreground">Subtotal</span>
									<Money {...order.subtotal} />
								</div>
								{/* HA-657: prices include VAT, so contained VAT is never labelled "Tax" (which reads as added on
								    top); an order rung before that, or an untaxed one, keeps its Tax row as before. */}
								{(taxParts.onTop > 0 || taxParts.included === 0) && (
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground">Tax</span>
										<span>
											{order.currency}{" "}
											{(
												(taxParts.onTop > 0 ? taxParts.onTop : order.tax) / 100
											).toFixed(2)}
										</span>
									</div>
								)}
								<Separator />
								<div className="flex justify-between font-medium">
									<span>Total</span>
									<Money {...order.total} />
								</div>
								{taxParts.included > 0 && (
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground">Includes VAT</span>
										<span>
											{order.currency} {(taxParts.included / 100).toFixed(2)}
										</span>
									</div>
								)}
								<div className="flex justify-between text-sm">
									<span className="text-muted-foreground">Total Paid</span>
									<Money {...order.totalPaid} />
								</div>
								{order.refundedTotal.value > 0 && (
									<>
										<Separator />
										<div className="flex justify-between text-sm text-orange-600">
											<span>Refunded</span>
											<Money {...order.refundedTotal} />
										</div>
										<div className="flex justify-between text-sm font-medium">
											<span>Refundable Balance</span>
											<Money {...order.refundableBalance} />
										</div>
									</>
								)}
							</div>

							{/* Close Reason */}
							{order.closeReason && (
								<div className="rounded-lg border p-3 space-y-2">
									<h4 className="text-sm font-medium">Close Reason</h4>
									<Separator />
									<div className="text-sm">
										{order.closeReason.replace(/([A-Z])/g, " $1").trim()}
									</div>
									{order.closeNotes && (
										<div className="text-sm text-muted-foreground">
											{order.closeNotes}
										</div>
									)}
								</div>
							)}

							{order.completedAt && (
								<div className="text-sm text-muted-foreground">
									Closed:{" "}
									<FormattedDate
										date={order.completedAt}
										timeZone={timeZone}
										options={{
											day: "2-digit",
											month: "2-digit",
											year: "numeric",
											hour: "2-digit",
											minute: "2-digit",
											hour12: false,
										}}
									/>
								</div>
							)}
						</div>
					</ScrollArea>
				) : null}

				<DialogFooter className="flex-col gap-2 sm:flex-col">
					{orderId && (
						<CheckOperation
							operation="staff-terminal-print-receipt"
							operations={operations}
						>
							<PrintReceiptButton
								orderId={orderId}
								variant="secondary"
								className="w-full h-12"
							/>
						</CheckOperation>
					)}
					<CheckOperation
						operation="staff-terminal-refund"
						operations={operations}
					>
						{isRefundable && (
							<Button
								variant="default"
								className="w-full h-12"
								onClick={handleProcessRefund}
							>
								<RotateCcw className="size-4 mr-2" />
								Process Refund
							</Button>
						)}
					</CheckOperation>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
